import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { CATEGORIES, findCategory } from "@/data/menu";
import { FlavorCard } from "@/components/FlavorCard";
import { CupcakeBuilder } from "@/components/CupcakeBuilder";

const CategoryList = ({ active, onPick }) => (
  <>
    <div className="no-scrollbar -mx-5 flex gap-2 overflow-x-auto px-5 pb-2 lg:hidden" data-testid="category-pills-mobile">
      {CATEGORIES.map((c) => (
        <button
          key={c.id}
          onClick={() => onPick(c.id)}
          data-testid={`category-pill-${c.id}`}
          className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-300 ${
            active === c.id ? "border-[#2D1E18] bg-[#2D1E18] text-white" : "border-[#F0DDD3] bg-white text-cacao"
          }`}
        >
          {c.name}
        </button>
      ))}
    </div>
    <aside className="sticky top-28 hidden self-start lg:block" data-testid="category-sidebar">
      {CATEGORIES.map((c, i) => (
        <button
          key={c.id}
          onClick={() => onPick(c.id)}
          data-testid={`category-tab-${c.id}`}
          className="group flex w-full items-baseline gap-4 border-b border-[#F0DDD3] py-3 text-left"
        >
          <span className={`f-mono text-xs ${active === c.id ? "text-rose" : "text-cacao-2"}`}>{String(i + 1).padStart(2, "0")}</span>
          <span className={`f-display text-xl transition-[color,transform] duration-300 group-hover:translate-x-1 ${active === c.id ? "italic text-rose" : "text-cacao"}`}>
            {c.name}
          </span>
        </button>
      ))}
    </aside>
  </>
);

const CategoryHeader = ({ cat }) => (
  <motion.div
    key={cat.id}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.6 }}
    className="relative mb-8 h-56 overflow-hidden rounded-[2rem] sm:h-72"
    data-testid="category-header"
  >
    <motion.img
      key={cat.image}
      src={cat.image}
      alt={cat.name}
      initial={{ scale: 1.12 }}
      animate={{ scale: 1 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,transparent_20%,rgba(45,30,24,0.75))]" />
    <div className="absolute bottom-0 left-0 p-6 sm:p-8">
      <p className="f-mono mb-2 text-xs uppercase tracking-[0.2em] text-[#FCE8E6]">{cat.tags.join(" • ")}</p>
      <h3 className="f-display text-4xl font-semibold leading-none text-white sm:text-5xl" data-testid="category-title">{cat.name}</h3>
    </div>
  </motion.div>
);

export const MenuBrowser = ({ active, setActive }) => {
  const [q, setQ] = useState("");
  const cat = findCategory(active);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return CATEGORIES.flatMap((c) => c.items.filter((n) => n.toLowerCase().includes(s) || c.name.toLowerCase().includes(s)).map((n) => ({ c, n })));
  }, [q]);

  return (
    <section id="menu" className="bg-[#FFF9F6] py-24 lg:py-32" data-testid="menu-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8 }}>
            <p className="f-mono mb-4 text-xs uppercase tracking-[0.25em] text-rose">Pick &amp; choose</p>
            <h2 className="f-display text-5xl font-semibold leading-[0.95] text-cacao sm:text-6xl">
              The full <span className="italic">menu</span>
            </h2>
            <p className="mt-4 max-w-md text-base text-cacao-2">Add any flavor to your box. Prices are quoted after you send your order.</p>
          </motion.div>
          <label className="flex w-full items-center gap-3 rounded-full border border-[#F0DDD3] bg-white px-5 py-3.5 lg:w-96">
            <Search className="h-4 w-4 text-cacao-2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search flavors… try “s'mores”"
              data-testid="menu-search-input"
              className="w-full bg-transparent text-sm text-cacao outline-none placeholder:text-[#8a756c]"
            />
            {q && (
              <button onClick={() => setQ("")} data-testid="menu-search-clear" aria-label="Clear search">
                <X className="h-4 w-4 text-cacao-2" />
              </button>
            )}
          </label>
        </div>

        {q ? (
          <div data-testid="search-results">
            <p className="f-mono mb-6 text-xs uppercase tracking-[0.2em] text-cacao-2">{results.length} matches</p>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {results.map(({ c, n }, i) => (
                <FlavorCard key={`${c.id}-${n}`} category={c} name={n} i={i} showCategory />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-10 lg:grid-cols-[280px_1fr] lg:gap-14">
            <CategoryList active={active} onPick={setActive} />
            <div>
              <CategoryHeader cat={cat} />
              {cat.builder ? (
                <CupcakeBuilder category={cat} />
              ) : (
                <div key={cat.id} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="flavor-grid">
                  {cat.items.map((n, i) => (
                    <FlavorCard key={n} category={cat} name={n} i={i} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
